import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IMiRecurso, EMiRecursoStatus, IDocenteMateria, IAlumnoEnrollment } from '../../types/miRecurso';

interface IMisRecursosPageState {
	recursos: IMiRecurso[];
	materias: IDocenteMateria[];
	alumnos: IAlumnoEnrollment[];
	selectedMateriaId: number | null;
	selectedStatus: EMiRecursoStatus | 'all';
	selectedDetailTab: 'recursos' | 'alumnos';
	searchQuery: string;
	pendingRemoveAlumno: IAlumnoEnrollment | null;
	uploadDialogOpen: boolean;
	addMateriaDialogOpen: boolean;
	codigoDialogMateriaId: number | null;
}

const initialState: IMisRecursosPageState = {
	recursos: [],
	materias: [],
	alumnos: [],
	selectedMateriaId: null,
	selectedStatus: 'all',
	selectedDetailTab: 'recursos',
	searchQuery: '',
	pendingRemoveAlumno: null,
	uploadDialogOpen: false,
	addMateriaDialogOpen: false,
	codigoDialogMateriaId: null,
};

export const misRecursosPageSlice = createSlice({
	name: 'misRecursosPage',
	initialState,
	reducers: {
		setRecursos: (state, action: PayloadAction<IMiRecurso[]>) => {
			state.recursos = action.payload;
		},
		addRecurso: (state, action: PayloadAction<IMiRecurso>) => {
			state.recursos.unshift(action.payload);
		},
		setMaterias: (state, action: PayloadAction<IDocenteMateria[]>) => {
			state.materias = action.payload;
		},
		addMateria: (state, action: PayloadAction<IDocenteMateria>) => {
			state.materias.push(action.payload);
		},
		incrementMateriaResourceCount: (state, action: PayloadAction<number>) => {
			const materia = state.materias.find((m) => m.id === action.payload);
			if (materia) materia.resourceCount += 1;
		},
		setAlumnos: (state, action: PayloadAction<IAlumnoEnrollment[]>) => {
			state.alumnos = action.payload;
		},
		removeAlumno: (state, action: PayloadAction<number>) => {
			state.alumnos = state.alumnos.filter((a) => a.id !== action.payload);
			// Decrement alumnoCount on the affected materia
			const enrollment = state.alumnos.find((a) => a.id === action.payload);
			if (enrollment) {
				const materia = state.materias.find((m) => m.id === enrollment.materiaId);
				if (materia && materia.alumnoCount > 0) materia.alumnoCount -= 1;
			}
		},
		decrementMateriaAlumnoCount: (state, action: PayloadAction<number>) => {
			const materia = state.materias.find((m) => m.id === action.payload);
			if (materia && materia.alumnoCount > 0) materia.alumnoCount -= 1;
		},
		setSelectedMateriaId: (state, action: PayloadAction<number | null>) => {
			state.selectedMateriaId = action.payload;
			state.selectedStatus = 'all';
			state.selectedDetailTab = 'recursos';
			state.searchQuery = '';
		},
		setSelectedStatus: (state, action: PayloadAction<EMiRecursoStatus | 'all'>) => {
			state.selectedStatus = action.payload;
		},
		setSelectedDetailTab: (state, action: PayloadAction<'recursos' | 'alumnos'>) => {
			state.selectedDetailTab = action.payload;
			state.searchQuery = '';
		},
		setSearchQuery: (state, action: PayloadAction<string>) => {
			state.searchQuery = action.payload;
		},
		setUploadDialogOpen: (state, action: PayloadAction<boolean>) => {
			state.uploadDialogOpen = action.payload;
		},
		setAddMateriaDialogOpen: (state, action: PayloadAction<boolean>) => {
			state.addMateriaDialogOpen = action.payload;
		},
		setCodigoDialogMateriaId: (state, action: PayloadAction<number | null>) => {
			state.codigoDialogMateriaId = action.payload;
		},
		setPendingRemoveAlumno: (state, action: PayloadAction<IAlumnoEnrollment | null>) => {
			state.pendingRemoveAlumno = action.payload;
		},
		updateMateriaCodigo: (state, action: PayloadAction<{ materiaId: number; newCodigo: string }>) => {
			const materia = state.materias.find((m) => m.id === action.payload.materiaId);
			if (materia) materia.codigo = action.payload.newCodigo;
		},
		resetMisRecursosPage: () => initialState,
	},
});

export const {
	setRecursos,
	addRecurso,
	setMaterias,
	addMateria,
	incrementMateriaResourceCount,
	setAlumnos,
	removeAlumno,
	decrementMateriaAlumnoCount,
	setSelectedMateriaId,
	setSelectedStatus,
	setSelectedDetailTab,
	setSearchQuery,
	setPendingRemoveAlumno,
	updateMateriaCodigo,
	setUploadDialogOpen,
	setAddMateriaDialogOpen,
	setCodigoDialogMateriaId,
	resetMisRecursosPage,
} = misRecursosPageSlice.actions;

export default misRecursosPageSlice.reducer;
